import { Maintenance } from '@/models/Maintenance';
import { doc, DocumentSnapshot, DocumentData, getDoc } from 'firebase/firestore';
import { defineStore } from 'pinia';
import { useFirestore } from 'vuefire';
import { logger } from '@/utils/logger'

export const useMaintenanceStore = defineStore('maintenance', {
    state: () => ({
        isLoading: false,
        lastUpdate: null as number | null,
        maintenanceSnapshot: null as DocumentSnapshot<DocumentData, DocumentData> | null,
        bypassToken: null as string | null,
    }),
    getters: {
        maintenance: (state): Maintenance | null => {
            if (!state.maintenanceSnapshot) return null;
            return Maintenance.fromFirebase(state.maintenanceSnapshot);
        },
        isMaintenanceEnabled: (state): boolean => {
            const m = (state.maintenanceSnapshot && Maintenance.fromFirebase(state.maintenanceSnapshot));
            return m?.enabled ?? false;
        },
        canAccessApp(): boolean {
            // Allow access if maintenance is disabled OR if bypass token is set
            return !this.isMaintenanceEnabled || !!this.bypassToken;
        },
    },
    actions: {
        /**
         * Reads system/maintenance, at most once per 3 minutes: every navigation
         * awaits this (router guard). `force` reads anyway: the maintenance
         * screen polls it to notice the end of the maintenance at once.
         */
        checkMaintenanceStatus: async function (force = false) {
            const expirationDate = Date.now() - (3 * 60 * 1000);
            if (!force && this.lastUpdate && this.lastUpdate > expirationDate) {
                // Read less than 3 minutes ago: every navigation awaits this check
                // (router guard), so a fresh read here delayed every page change
                // by a Firestore round trip (the branch used to fall through).
                return
            }
            this.isLoading = true;

            try {
                const maintenanceDoc = doc(useFirestore(), 'system', 'maintenance');
                const snapshot = await getDoc(maintenanceDoc);
                this.maintenanceSnapshot = snapshot;
                this.lastUpdate = Date.now();
                this.isLoading = false;
            } catch {
                // Handle document not existing or permission errors gracefully
                // Treat as maintenance disabled (no document = no maintenance)
                logger.info('Maintenance document not found or inaccessible, treating as disabled');
                this.maintenanceSnapshot = null;
                this.isLoading = false;
            }
        },
        setBypassToken: function (token: string | null) {
            this.bypassToken = token;
        },
    },
});

